import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, FileCode, Send, Users, BarChart3, Settings, LogOut, Package, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { subscribeRealtime } from "@/lib/realtime";
import PresenceToggle from "@/components/PresenceToggle";

const NAV = [
  { to: "/inbox", label: "Shared Inbox", icon: MessageSquare, roles: ["agent", "admin"] },
  { to: "/templates", label: "Meta Templates", icon: FileCode, roles: ["admin"] },
  { to: "/broadcast", label: "Broadcasting", icon: Send, roles: ["admin"] },
  { to: "/contacts", label: "Contacts & Tags", icon: Users, roles: ["admin"] },
  { to: "/products", label: "Product Databank", icon: Package, roles: ["admin"] },
  { to: "/dashboard", label: "Dashboard", icon: BarChart3, roles: ["admin"] },
  { to: "/settings", label: "API & Settings", icon: Settings, roles: ["admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("cd_nav_collapsed") === "1");
  const prevRef = useRef(null);
  const audioRef = useRef(null);

  const toggleNav = () => {
    setCollapsed((c) => {
      localStorage.setItem("cd_nav_collapsed", c ? "0" : "1");
      return !c;
    });
  };

  const playPing = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = audioRef.current || (audioRef.current = new Ctx());
      if (ctx.state === "suspended") ctx.resume();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(880, ctx.currentTime);
      o.frequency.setValueAtTime(1320, ctx.currentTime + 0.09);
      g.gain.setValueAtTime(0.18, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.45);
      o.connect(g);
      g.connect(ctx.destination);
      o.start();
      o.stop(ctx.currentTime + 0.5);
    } catch (e) {}
  };

  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get("/conversations");
        const total = data.reduce((s, c) => s + (c.unread_count || 0), 0);
        if (prevRef.current !== null && total > prevRef.current) {
          playPing();
          toast("💬 New WhatsApp message", { description: "A customer message just arrived in the inbox" });
        }
        prevRef.current = total;
        setUnread(total);
        document.title = total > 0 ? `(${total}) Connect Desk` : "Connect Desk";
      } catch (e) {}
    };
    check();
    const unsub = subscribeRealtime((ev) => {
      if (ev.type === "message") check();
    });
    const t = setInterval(check, 30000);
    return () => { clearInterval(t); unsub(); };
  }, []);

  return (
    <div className="h-screen flex flex-col" style={{ background: "var(--cd-cream)" }}>
      <header className="flex items-center justify-between px-5 py-2.5 border-b-2 shrink-0 bg-white/70 backdrop-blur-sm" style={{ borderColor: "var(--cd-navy)" }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center border-2" style={{ background: "var(--cd-yellow)", borderColor: "var(--cd-navy)", boxShadow: "2px 2px 0 var(--cd-navy)" }}>
            <MessageSquare size={18} strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading font-extrabold text-lg leading-tight" data-testid="app-brand">Connect Desk</div>
            <a href="https://crm.gosnd.com" data-testid="snd-crm-link" className="font-hand text-sm text-amber-800 leading-none -mt-0.5 block hover:underline hover:text-amber-950 transition-colors" title="Back to SND CRM">SND CRM ↗</a>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {user?.role === "agent" && <PresenceToggle />}
          <span className="hidden md:flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border-2" style={{ background: "var(--cd-green)", borderColor: "var(--cd-navy)" }}>
            <span className="w-2 h-2 rounded-full bg-green-600 animate-pulse" /> WhatsApp API Connected
          </span>
          <div className="flex items-center gap-2 pl-3 border-l-2" style={{ borderColor: "var(--cd-navy)" }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2" style={{ background: "var(--cd-pink)", borderColor: "var(--cd-navy)" }}>
              {(user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
            </div>
            <div className="hidden sm:block">
              <div className="text-sm font-bold leading-tight" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{user?.role}</div>
            </div>
            <button data-testid="logout-btn" className="btn-ghost2" onClick={async () => { await logout(); navigate("/login"); }}>
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>
      <div className="flex flex-1 min-h-0">
        <nav className={`${collapsed ? "w-14 px-1.5" : "w-52 px-3"} shrink-0 border-r-2 py-3 flex flex-col gap-1.5 overflow-y-auto transition-all duration-200`} style={{ borderColor: "var(--cd-navy)" }}>
          <button data-testid="nav-collapse-toggle" className={`btn-ghost2 mb-1 ${collapsed ? "justify-center" : "self-end"}`} onClick={toggleNav} title={collapsed ? "Expand menu" : "Collapse menu"}>
            {collapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
          </button>
          {NAV.filter((n) => n.roles.includes(user?.role)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              title={n.label}
              data-testid={`nav-${n.to.slice(1)}`}
              className={({ isActive }) =>
                `relative flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold font-heading border-2 transition-all ${
                  collapsed ? "justify-center px-0" : ""
                } ${isActive ? "text-white" : "border-transparent hover:bg-black/5"}`
              }
              style={({ isActive }) =>
                isActive
                  ? { background: "var(--cd-navy)", borderColor: "var(--cd-navy)", boxShadow: "3px 3px 0 rgba(19,30,58,0.3)" }
                  : {}
              }
            >
              <n.icon size={17} className="shrink-0" />
              {!collapsed && n.label}
              {n.to === "/inbox" && unread > 0 && (
                collapsed ? (
                  <span data-testid="inbox-unread-badge" className="absolute -top-1 -right-1 min-w-4 h-4 px-0.5 rounded-full text-[9px] font-bold flex items-center justify-center text-white" style={{ background: "#25D366" }}>
                    {unread}
                  </span>
                ) : (
                  <span data-testid="inbox-unread-badge" className="ml-auto min-w-5 h-5 px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white" style={{ background: "#25D366" }}>
                    {unread}
                  </span>
                )
              )}
            </NavLink>
          ))}
          {!collapsed && (
            <div className="mt-auto px-3 pt-4">
              <div className="font-hand text-base text-amber-800 rotate-[-2deg]">your whatsapp artboard ✦</div>
            </div>
          )}
        </nav>
        <main className="flex-1 min-w-0 min-h-0 overflow-hidden">{children}</main>
      </div>
    </div>
  );
}
